/**
 * Module-level coordination for stacked <Sheet>s.
 *
 * When sheets nest (Job → Recurrence, Invoice → PDF preview, Settings →
 * rationale sheet) only the *topmost* one may own keyboard behaviour —
 * Esc-to-close and the focus-trap. A naive "every sheet traps focus"
 * approach makes nested sheets fight each other. Each Sheet pushes its id
 * on mount and pops on unmount; `isTopmost` tells a Sheet whether it's the
 * active layer. Subscribers re-render when the stack changes.
 *
 * Background scroll-lock is ref-counted here too so a nested sheet closing
 * doesn't unlock the page while an ancestor sheet is still open.
 */

const stack: string[] = [];
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

export function pushSheet(id: string): void {
  stack.push(id);
  emit();
}

export function popSheet(id: string): void {
  const i = stack.lastIndexOf(id);
  if (i !== -1) stack.splice(i, 1);
  emit();
}

/** How many sheets are open. Back (a swipe, Android's button) closes a sheet before it leaves a page. */
export function sheetStackDepth(): number {
  return stack.length;
}

export function isTopmost(id: string): boolean {
  return stack.length > 0 && stack[stack.length - 1] === id;
}

export function subscribeSheetStack(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

// ── Background scroll-lock, ref-counted across nested sheets ──────────────
let lockCount = 0;
let savedOverflow = '';

export function lockBodyScroll(): () => void {
  if (typeof document === 'undefined') return () => {};
  if (lockCount === 0) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  lockCount += 1;

  let released = false;
  return () => {
    if (released) return;
    released = true;
    lockCount -= 1;
    if (lockCount === 0) document.body.style.overflow = savedOverflow;
  };
}
